// "More below" affordance for the pinned leaderboard rail.
//
// The rail is height-capped to the band, so rows past the fold are reachable
// only by scrolling inside the card — and the scrollbars are deliberately
// hidden, so without this there is no signal that more rows exist.
//
// Rendered as three nested chevrons that pulse downward in sequence (⌄⌄⌄),
// which reads as "keep going down" without needing a text label. Tapping it
// scrolls the list to the bottom. Hides itself once the user has scrolled.

import React, {useEffect, useRef} from 'react';
import {Animated, Easing, Pressable, StyleSheet, View} from 'react-native';
import MaterialCommunityIcon from 'react-native-vector-icons/MaterialCommunityIcons';
import {lbColors} from '@/Screens/Leaderboard/leaderboardTheme';

const CHEVRONS = [0, 1, 2];
// Each chevron peaks this many ms after the one above it, producing the
// downward cascade rather than three things blinking in unison.
const STAGGER_MS = 170;
const FADE_MS = 380;

export default function ScrollHint({
  visible,
  onPress,
}: {
  visible: boolean;
  onPress?: () => void;
}) {
  const fade = useRef(new Animated.Value(0)).current;
  // One value per chevron so they can be phase-offset.
  const waves = useRef(CHEVRONS.map(() => new Animated.Value(0.35))).current;
  const drift = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(fade, {
      toValue: visible ? 1 : 0,
      duration: 220,
      useNativeDriver: true,
    }).start();
  }, [visible, fade]);

  useEffect(() => {
    if (!visible) return undefined;

    const loops = waves.map((value, i) =>
      Animated.loop(
        Animated.sequence([
          Animated.delay(i * STAGGER_MS),
          Animated.timing(value, {
            toValue: 1,
            duration: FADE_MS,
            easing: Easing.out(Easing.quad),
            useNativeDriver: true,
          }),
          Animated.timing(value, {
            toValue: 0.35,
            duration: FADE_MS,
            easing: Easing.in(Easing.quad),
            useNativeDriver: true,
          }),
          // Pad the tail so every chevron's cycle is the same length and the
          // cascade stays in step instead of drifting out of phase over time.
          Animated.delay((CHEVRONS.length - 1 - i) * STAGGER_MS),
        ]),
      ),
    );

    // Whole group eases down and back, so it reads as movement, not a blink.
    const driftLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(drift, {
          toValue: 1,
          duration: 900,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(drift, {
          toValue: 0,
          duration: 900,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
      ]),
    );

    loops.forEach(l => l.start());
    driftLoop.start();
    return () => {
      loops.forEach(l => l.stop());
      driftLoop.stop();
    };
  }, [visible, waves, drift]);

  const translateY = drift.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 4],
  });

  return (
    <Animated.View
      // box-none so the rows underneath stay scrollable/clickable through the
      // overlay, while the arrows themselves remain tappable.
      pointerEvents={visible ? 'box-none' : 'none'}
      style={[styles.wrap, {opacity: fade}]}>
      <Pressable
        onPress={onPress}
        hitSlop={8}
        style={({pressed}) => [styles.hit, pressed && styles.hitPressed]}>
        <Animated.View style={{transform: [{translateY}]}}>
          {CHEVRONS.map(i => (
            <Animated.View
              key={i}
              style={[
                styles.chevron,
                i > 0 && styles.chevronStacked,
                {opacity: waves[i]},
              ]}>
              <MaterialCommunityIcon
                name="chevron-down"
                size={26}
                color={lbColors.goldDeep}
              />
            </Animated.View>
          ))}
        </Animated.View>
      </Pressable>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    paddingBottom: 2,
  },
  hit: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingTop: 6,
    paddingBottom: 2,
    cursor: 'pointer',
    // Fades the rows out behind the trail so the arrows stay readable over
    // both the plain and the highlighted (flash / "you") row backgrounds.
    backgroundColor: 'rgba(255,255,255,0.82)',
    borderRadius: 99,
  },
  hitPressed: {opacity: 0.6},
  chevron: {alignItems: 'center', justifyContent: 'center', height: 15},
  // Negative offset nests them tightly, so they read as one ⌄⌄⌄ arrow trail
  // rather than three separate icons.
  chevronStacked: {marginTop: -8},
});
